import React, { useState, useMemo, useEffect } from 'react'
import debounce from 'lodash.debounce'
import { Draftail } from '../../../components/draftail'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-html'
import 'ace-builds/src-noconflict/theme-tomorrow'
import 'ace-builds/src-noconflict/ext-searchbox'
import axios from 'axios'
import { apiUrl } from '../../../utils/url'
import { getJwt } from '../../../utils/jwt'
import { buildHtmlEmail } from '../../../utils/email'
import { Button, Col, Row } from 'reactstrap'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog } from '@fortawesome/pro-duotone-svg-icons'

function Email({
  initialHtml,
  itemCode,
  setContentHtml,
  setAssembledHtml,
  emailTemplate,
  options,
  keywordsEnabled,
  externalKey,
  sendEmailTest,
  emailTemplateStatus,
  launched,
}) {
  // State for our Assembled Email Template while in this component
  const [internalAssembledHtml, setInternalAssembledHtml] =
    useState(emailTemplate)
  // Modifier To Force Editor Render
  const [keyModifier, setKeyModifier] = useState(0)
  // Code or Rich Text Editor
  const [codeEditor, setCodeEditor] = useState(false)
  // State for loading while parsing/building HTML
  const [loading, setLoading] = useState(false)

  // The actual function that makes API calls
  const performContentUpdate = async (html) => {
    setLoading(true)

    let emailContentHtml = html
    const axiosOptions = {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }

    try {
      // Parse Placeholder Mappings
      const { data: contentWithMappings } = await axios.post(
        `${apiUrl}broadcasts/parse-placeholders`,
        {
          content: emailContentHtml,
          method: 'ADD',
        },
        axiosOptions,
      )

      emailContentHtml = contentWithMappings

      if (keywordsEnabled) {
        const { data: contentWithKeywords } = await axios.post(
          `${apiUrl}decision-vision/get-keywords`,
          {
            list: itemCode,
            content: emailContentHtml,
          },
          axiosOptions,
        )

        emailContentHtml = contentWithKeywords.content
      }

      // Build Assembled HTML using emailTemplate
      const assembledHtmlEmail = buildHtmlEmail(
        emailContentHtml,
        emailTemplate || '$content',
        options,
      )

      setInternalAssembledHtml(assembledHtmlEmail)
      setAssembledHtml(assembledHtmlEmail)
      setContentHtml(html)
    } catch (error) {
      console.error(error)
      toast.error('Error updating email. Please check the console for details.')
    } finally {
      setLoading(false)
    }
  }

  // Create debounced version of the update function
  const debouncedUpdateContentHtml = useMemo(() => {
    return debounce(performContentUpdate, 500)
  }, [keywordsEnabled, emailTemplate, options])

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateContentHtml.cancel()
    }
  }, [debouncedUpdateContentHtml])

  // Handler for immediate state updates when user types
  const updateContentHtml = (html) => {
    debouncedUpdateContentHtml(html)
  }

  // Update Assembled HTML
  const updateAssembledHtml = (html) => {
    setAssembledHtml(html)
    setInternalAssembledHtml(html)
  }

  // Switch to Code Editor
  const toggleCodeEditor = async () => {
    if (codeEditor) {
      setKeyModifier((old) => old + 1)
      setCodeEditor(false)
    } else {
      setCodeEditor(true)
    }
  }

  return (
    <Col>
      <Row>
        <Col>
          <p>Changes made here will be reflected in the email only...</p>
        </Col>
      </Row>
      <Row className="mb-3 d-flex align-items-center">
        {emailTemplateStatus && (
          <Col>
            <p className="mb-0">
              To view the email template in Blueshift,{' '}
              <a
                href={`https://app.getblueshift.com/dashboard#/app/email_template_studio/${emailTemplateStatus.uuid}/edit/templates/html`}
                target="_blank"
                rel="noreferrer"
              >
                click here
              </a>
              .
            </p>
          </Col>
        )}
        <Col>
          <Button
            className="float-end ms-2"
            onClick={toggleCodeEditor}
            color="cpblue-300"
          >
            {codeEditor ? 'Edit Rich Text' : 'Edit HTML'}
          </Button>
          <Button
            onClick={() => {
              if (
                !internalAssembledHtml ||
                internalAssembledHtml.length === 0
              ) {
                toast.warning('Assembled email content is missing...')
              } else {
                setAssembledHtml(internalAssembledHtml)
                sendEmailTest()
              }
            }}
            color="cpblue-500"
            className="float-end"
            disabled={
              !internalAssembledHtml ||
              internalAssembledHtml.length === 0 ||
              launched ||
              loading
            }
          >
            {!internalAssembledHtml || internalAssembledHtml.length === 0
              ? 'No Content to Send'
              : 'Send Test Email'}
          </Button>
          {loading && (
            <Col className="text-cpblue-500">
              <FontAwesomeIcon icon={faCog} spin size="sm" /> Building email...
            </Col>
          )}
        </Col>
      </Row>
      {codeEditor ? (
        <AceEditor
          fontSize={16}
          showPrintMargin={false}
          wrapEnabled={true}
          width="100%"
          mode="html"
          theme={'tomorrow'}
          name="email-content-ace-editor"
          editorProps={{ $blockScrolling: true }}
          value={internalAssembledHtml}
          onChange={updateAssembledHtml}
          setOptions={{
            indentedSoftWrap: false,
            printMarginColumn: 90,
            useWorker: false,
          }}
        />
      ) : (
        <Draftail
          key={`rich-email-editor-${keyModifier}-${externalKey}`}
          initialHtml={initialHtml}
          setHtml={updateContentHtml}
          showTopToolbar={true}
        />
      )}
    </Col>
  )
}

export default Email
